import type { WorkoutRepository } from '../../../application/ports/WorkoutRepository';
import type { ProfileId, WorkoutSessionId } from '../../../domain/shared/ids';
import type { WorkoutSession } from '../../../domain/workout/WorkoutSession';
import type { AscendInvictusDatabase } from '../AscendInvictusDatabase';
import { WorkoutSessionMapper } from '../mappers';
import { executePersistence } from '../translatePersistenceError';

export class DexieWorkoutRepository implements WorkoutRepository {
  public constructor(private readonly database: AscendInvictusDatabase) {}

  public save(session: WorkoutSession): Promise<void> {
    return executePersistence('write', async () => {
      await this.database.workoutSessions.put(WorkoutSessionMapper.toRecord(session));
    });
  }

  public findById(id: WorkoutSessionId): Promise<WorkoutSession | null> {
    return executePersistence('read', async () => {
      const record = await this.database.workoutSessions.get(id);
      return record ? WorkoutSessionMapper.fromRecord(record) : null;
    });
  }

  public findByLogicalDate(
    profileId: ProfileId,
    logicalDate: string,
  ): Promise<WorkoutSession | null> {
    return executePersistence('read', async () => {
      const record = await this.database.workoutSessions
        .where('[profileId+logicalDate]')
        .equals([profileId, logicalDate])
        .first();
      return record ? WorkoutSessionMapper.fromRecord(record) : null;
    });
  }

  public listByProfile(profileId: ProfileId): Promise<readonly WorkoutSession[]> {
    return executePersistence('read', async () =>
      (await this.database.workoutSessions.where('profileId').equals(profileId).toArray()).map(
        (record) => WorkoutSessionMapper.fromRecord(record),
      ),
    );
  }

  public delete(id: WorkoutSessionId): Promise<void> {
    return executePersistence('write', async () => {
      await this.database.workoutSessions.delete(id);
    });
  }

  public clearForProfile(profileId: ProfileId): Promise<void> {
    return executePersistence('write', async () => {
      await this.database.workoutSessions.where('profileId').equals(profileId).delete();
    });
  }
}
